import { useEffect, useState } from "react";

import { Redirect } from "react-router-dom";

import {
  ChannelListFilters,
  getChannelListData,
} from "@/api/endpoints/channels";
import useSearchParams from "@/hooks/useSearchParams";
import { useUser } from "@/hooks/useUser";
import { ROUTES } from "@/routes_new";
import { Channel } from "@/types/Channel";
import Container from "@components/Container";
import PaginationNew from "@/components/PaginationNew";
import Rhythm from "@components/Rhythm";
import SpinnerAnimated from "@components/SpinnerAnimated";
import Typography from "@components/Typography";

import Breadcrumbs from "../Breadcrumbs";
import TranscriptStatusExplainer from "../TranscriptStatusExplainer";
import ChannelListFilter from "./ChannelListFilter";
import ChannelListTable from "./ChannelListTable";

const ChannelListView: React.FunctionComponent = () => {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_count: 1,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams<ChannelListFilters>();
  const MAX_PRESENTABLE_CHANNEL_COUNT = 10000;

  const { page = 1 } = searchParams;

  const { data: userData, isLoading: isLoadingUser } = useUser();
  const isStaffUser = userData?.ok ? userData.data.is_staff : false;

  useEffect(() => {
    const fetchChannels = () => {
      setLoading(true);

      getChannelListData(searchParams)
        .then((response) => {
          if (!response.ok) {
            throw new Error(response.statusText);
          }
          return response.data;
        })
        .then((data) => {
          if (data) {
            const { results, count, page, page_count } = data;

            if (results) {
              setChannels(results);
            }

            setPagination({ count, page, page_count });
          }
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchChannels();
  }, [searchParams]);

  if (isLoadingUser) return null;
  if (!isStaffUser) return <Redirect to={ROUTES.APP_HOME} />;

  return (
    <Container>
      <Rhythm>
        <Breadcrumbs />
      </Rhythm>
      <Rhythm>
        <Typography variant="h1">Channels</Typography>
        <ChannelListFilter />
      </Rhythm>
      <Rhythm>
        {pagination.count !== null && (
          <p>
            {pagination.count >= MAX_PRESENTABLE_CHANNEL_COUNT
              ? "More than " + MAX_PRESENTABLE_CHANNEL_COUNT
              : "Displaying " + pagination.count}{" "}
            channels
          </p>
        )}
      </Rhythm>
      <Rhythm>
        <ChannelListTable channels={channels} />

        {loading ? <SpinnerAnimated fill="currentColor" width="2rem" /> : null}
      </Rhythm>
      <Rhythm>
        <TranscriptStatusExplainer precedingText="Applies to the last 30 days:" />
      </Rhythm>
      <Rhythm>
        {pagination.page_count > 1 ? (
          <PaginationNew
            page={page}
            max={pagination.page_count}
            onChangePage={(nextPage) => setSearchParams({ page: nextPage })}
          />
        ) : null}
      </Rhythm>
    </Container>
  );
};

export default ChannelListView;
